import { X, TrendingUp, Activity, Eye, MousePointer, Target, Zap, DollarSign, Globe, BarChart3, Percent, AlertCircle } from 'lucide-react';
import { fmt } from '../utils/helpers';

interface CountryDetailModalProps {
    country: any;
    onClose: () => void;
}

export function CountryDetailModal({ country, onClose }: CountryDetailModalProps) {
    if (!country) return null;

    // Calcular métricas agregadas
    const fillRate = country.impressions > 0
        ? ((country.impressions / (country.impressions + country.unfilled_impressions)) * 100)
        : 0;

    // Separar domínios ativos e inativos
    const activeDomains = country.domains?.filter((d: any) => d.impressions > 0) || [];
    const totalDomains = country.domains?.length || 0;

    // Pegar top 5 ad blocks
    const topAdBlocks = country.top_ad_blocks?.slice(0, 5) || [];

    // Função para determinar o tipo de ad block
    const getBlockType = (blockName: string) => {
        const name = blockName.toLowerCase();
        if (name.includes('interstitial')) return { label: 'Interstitial', color: 'purple', icon: Eye };
        if (name.includes('rewarded')) return { label: 'Rewarded', color: 'yellow', icon: Zap };
        if (name.includes('anchor') || name.includes('topo')) return { label: 'Anchor', color: 'blue', icon: MousePointer };
        if (name.includes('content')) return { label: 'Content', color: 'green', icon: Activity };
        if (name.includes('trigger')) return { label: 'Trigger', color: 'orange', icon: Target };
        return { label: 'Other', color: 'gray', icon: Activity };
    };

    // Calcular performance geral
    const getPerformanceRating = () => {
        const score = (country.pmr * 0.4) + (country.ctr * 2) + (fillRate * 0.3);
        if (score >= 80) return { label: 'Excelente', color: 'green', icon: TrendingUp };
        if (score >= 60) return { label: 'Boa', color: 'blue', icon: Activity };
        if (score >= 40) return { label: 'Regular', color: 'yellow', icon: Activity };
        return { label: 'Baixa', color: 'orange', icon: AlertCircle };
    };

    const performance = getPerformanceRating();
    const PerformanceIcon = performance.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
                {/* Header Moderno */}
                <div className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 px-8 py-6">
                    {/* Decorative Background */}
                    <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,transparent)]"></div>

                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Flag Circle */}
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-3xl border-2 border-white/30 shadow-xl">
                                🌍
                            </div>

                            {/* Title & Info */}
                            <div>
                                <h2 className="text-3xl font-bold text-white mb-1">{country.country}</h2>
                                <div className="flex items-center gap-3">
                                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                                        {activeDomains.length} de {totalDomains} domínios ativos
                                    </span>
                                    <span className={`px-3 py-1 bg-${performance.color}-500/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30 flex items-center gap-1`}>
                                        <PerformanceIcon className="h-3 w-3" />
                                        Performance: {performance.label}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white hover:bg-white/20 p-2.5 rounded-xl transition-all"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content - Grid Layout */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-8">
                        {/* KPIs Grid - Destaque */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            {/* Receita */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border-2 border-emerald-200 group hover:shadow-lg transition-all">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform"></div>
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-emerald-500 rounded-lg">
                                            <DollarSign className="h-4 w-4 text-white" />
                                        </div>
                                        <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Receita</span>
                                    </div>
                                    <p className="text-3xl font-bold text-emerald-700 mb-1">{fmt.currency(country.revenue)}</p>
                                    <p className="text-xs text-emerald-600">Cliente: {fmt.currency(country.revenue_client)}</p>
                                </div>
                            </div>

                            {/* Impressões */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border-2 border-blue-200 group hover:shadow-lg transition-all">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform"></div>
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-blue-500 rounded-lg">
                                            <Eye className="h-4 w-4 text-white" />
                                        </div>
                                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Impressões</span>
                                    </div>
                                    <p className="text-3xl font-bold text-blue-700 mb-1">{fmt.number(country.impressions)}</p>
                                    <p className="text-xs text-blue-600">Fill Rate: {fillRate.toFixed(1)}%</p>
                                </div>
                            </div>

                            {/* CTR */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border-2 border-purple-200 group hover:shadow-lg transition-all">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform"></div>
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-purple-500 rounded-lg">
                                            <MousePointer className="h-4 w-4 text-white" />
                                        </div>
                                        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">CTR</span>
                                    </div>
                                    <p className="text-3xl font-bold text-purple-700 mb-1">{fmt.percent(country.ctr)}</p>
                                    <p className="text-xs text-purple-600">{fmt.number(country.clicks)} cliques</p>
                                </div>
                            </div>

                            {/* eCPM */}
                            <div className="relative overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-5 border-2 border-orange-200 group hover:shadow-lg transition-all">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform"></div>
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-orange-500 rounded-lg">
                                            <Target className="h-4 w-4 text-white" />
                                        </div>
                                        <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">eCPM</span>
                                    </div>
                                    <p className="text-3xl font-bold text-orange-700 mb-1">{fmt.currency(country.ecpm)}</p>
                                    <p className="text-xs text-orange-600">PMR: {fmt.percent(country.pmr)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Seção Dividida: Domínios e Ad Blocks */}
                        <div className="grid lg:grid-cols-2 gap-8 mb-8">
                            {/* Domínios Performance */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <Globe className="h-5 w-5 text-emerald-600" />
                                        Domínios
                                    </h3>
                                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                                        {activeDomains.length} ativos
                                    </span>
                                </div>

                                <div className="space-y-3">
                                    {activeDomains.slice(0, 5).map((domain: any, idx: number) => (
                                        <div
                                            key={idx}
                                            className="group bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-md transition-all"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    {/* Rank Badge */}
                                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                                                        idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-md' :
                                                            idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700 shadow-md' :
                                                                idx === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400 text-white shadow-md' :
                                                                    'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {idx + 1}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-bold text-gray-900 text-sm truncate mb-1" title={domain.domain}>
                                                            {domain.domain}
                                                        </p>
                                                        <p className="text-xl font-bold text-emerald-600">
                                                            {fmt.currency(domain.revenue)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Métricas em Grid */}
                                            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500">Impressões</p>
                                                    <p className="text-sm font-bold text-gray-900">{fmt.number(domain.impressions)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500">CTR</p>
                                                    <p className="text-sm font-bold text-purple-600">{fmt.percent(domain.ctr)}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500">eCPM</p>
                                                    <p className="text-sm font-bold text-orange-600">{fmt.currency(domain.ecpm)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Ver todos os domínios */}
                                {activeDomains.length > 5 && (
                                    <button className="w-full mt-3 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors">
                                        + Ver mais {activeDomains.length - 5} domínios
                                    </button>
                                )}
                            </div>

                            {/* Ad Blocks Performance */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <BarChart3 className="h-5 w-5 text-blue-600" />
                                        Top Ad Blocks
                                    </h3>
                                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                                        {topAdBlocks.length} blocos
                                    </span>
                                </div>

                                <div className="space-y-3">
                                    {topAdBlocks.map((block: any, idx: number) => {
                                        const blockType = getBlockType(block.block);
                                        const BlockIcon = blockType.icon;

                                        return (
                                            <div
                                                key={idx}
                                                className="group bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all"
                                            >
                                                <div className="flex items-start gap-3 mb-3">
                                                    {/* Icon & Type */}
                                                    <div className={`p-2.5 bg-${blockType.color}-100 rounded-lg`}>
                                                        <BlockIcon className={`h-4 w-4 text-${blockType.color}-600`} />
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className={`px-2 py-0.5 bg-${blockType.color}-100 text-${blockType.color}-700 text-xs font-bold rounded`}>
                                                                {blockType.label}
                                                            </span>
                                                            <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>
                                                        </div>
                                                        <p className="font-semibold text-gray-900 text-xs truncate mb-1" title={block.block}>
                                                            {block.block.split('_')[0]}
                                                        </p>
                                                        <p className="text-lg font-bold text-blue-600">
                                                            {fmt.currency(block.revenue)}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Métricas Inline */}
                                                <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-100">
                                                    <div className="flex items-center gap-1">
                                                        <Eye className="h-3 w-3 text-gray-400" />
                                                        <span className="font-semibold text-gray-900">{fmt.number(block.impressions)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <MousePointer className="h-3 w-3 text-gray-400" />
                                                        <span className="font-semibold text-gray-900">{fmt.number(block.clicks)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Percent className="h-3 w-3 text-purple-500" />
                                                        <span className="font-semibold text-purple-600">{fmt.percent(block.ctr)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Target className="h-3 w-3 text-orange-500" />
                                                        <span className="font-semibold text-orange-600">{fmt.currency(block.ecpm)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Métricas Avançadas */}
                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Activity className="h-5 w-5 text-gray-600" />
                                Métricas Avançadas
                            </h3>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white rounded-xl p-4 border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1 font-medium">Requisições Servidas</p>
                                    <p className="text-2xl font-bold text-gray-900">{fmt.number(country.requests_served)}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1 font-medium">Req. Elegíveis</p>
                                    <p className="text-2xl font-bold text-gray-900">{fmt.number(country.elegible_ad_request)}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1 font-medium">Não Preenchidas</p>
                                    <p className="text-2xl font-bold text-orange-600">{fmt.number(country.unfilled_impressions)}</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 border border-gray-200">
                                    <p className="text-xs text-gray-600 mb-1 font-medium">Fill Rate</p>
                                    <p className="text-2xl font-bold text-emerald-600">{fillRate.toFixed(1)}%</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}